import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X, AlertCircle } from 'lucide-react';
import { referenceService } from '@/services/referenceService';
import { Icd10Code } from '@/types/reference.types';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/cn';
import { useToast } from '@/hooks/use-toast';

interface ICD10AutocompleteProps {
  value?: string; // ICD-10 code (e.g., "Q21.0")
  onChange: (code: string | null, description?: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
  className?: string;
}

export function ICD10Autocomplete({
  value,
  onChange,
  placeholder = 'Cauta cod ICD-10 sau descriere...',
  isDisabled = false,
  className
}: ICD10AutocompleteProps) {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [codes, setCodes] = useState<Icd10Code[]>([]);
  const [selectedCode, setSelectedCode] = useState<Icd10Code | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load ICD-10 codes on mount
  useEffect(() => {
    loadCodes();
  }, []);

  // Set initial selected code based on value prop
  useEffect(() => {
    if (value && codes.length > 0) {
      const code = codes.find(c => c.icd10_code === value);
      if (code) {
        setSelectedCode(code);
        setSearchTerm('');
      }
    } else if (!value) {
      setSelectedCode(null);
      setSearchTerm('');
    }
  }, [value, codes]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadCodes = async () => {
    setIsLoading(true);
    try {
      const data = await referenceService.getIcd10ChdCodes();
      // COMPLEX* codes go at the end of the list
      const isComplex = (c: { icd10_code: string }) => c.icd10_code.toUpperCase().startsWith('COMPLEX');
      setCodes([...data.filter(c => !isComplex(c)), ...data.filter(isComplex)]);
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut incarca codurile ICD-10. Va rugam incercati din nou.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Filter codes based on search term
  const filteredCodes = useMemo(() => {
    if (!searchTerm.trim()) {
      return codes;
    }

    const term = searchTerm.toLowerCase().trim();
    return codes.filter(code =>
      code.icd10_code.toLowerCase().includes(term) ||
      code.description_ro.toLowerCase().includes(term) ||
      (code.description_en && code.description_en.toLowerCase().includes(term))
    );
  }, [codes, searchTerm]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(true);
    setHighlightedIndex(-1);

    // Clear selection if user is typing
    if (selectedCode) {
      setSelectedCode(null);
      onChange(null);
    }
  };

  const handleSelectCode = (code: Icd10Code) => {
    setSelectedCode(code);
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange(code.icd10_code, code.description_ro);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    setSelectedCode(null);
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange(null);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && e.key !== 'Escape') {
      setIsOpen(true);
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredCodes.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredCodes.length) {
          handleSelectCode(filteredCodes[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      }
    }
  }, [highlightedIndex]);

  const displayValue = selectedCode
    ? `${selectedCode.icd10_code} - ${selectedCode.description_ro}`
    : searchTerm;

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isDisabled || isLoading}
          className={cn(
            'pl-10 pr-10',
            selectedCode && 'font-medium'
          )}
        />
        {selectedCode && !isDisabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
            onClick={handleClear}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && !isDisabled && (
        <div
          ref={dropdownRef}
         // className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-md max-h-[300px] overflow-y-auto"
          className=""
        >
          {isLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Se incarca...
            </div>
          ) : filteredCodes.length === 0 ? (
            <div className="p-4 text-center">
              <AlertCircle className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {searchTerm ? 'Nu s-au gasit rezultate' : 'Nu sunt coduri disponibile'}
              </p>
            </div>
          ) : (
            filteredCodes.map((code, index) => (
              <div
                key={code.id}
                className={cn(
                  'px-3 py-2 cursor-pointer text-sm transition-colors text-foreground',
                  'hover:bg-accent hover:text-accent-foreground',
                  highlightedIndex === index && 'bg-accent text-accent-foreground',
                  selectedCode?.id === code.id && 'bg-primary/10 font-medium'
                )}
                onClick={() => handleSelectCode(code)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground">
                      {code.icd10_code}
                    </div>
                    <div className="text-foreground/70 truncate">
                      {code.description_ro}
                    </div>
                  </div>
                  {Number(code.is_severe_chd) > 0 && (
                    <span className="text-xs px-2 py-0.5 bg-destructive/10 text-destructive rounded-full whitespace-nowrap">
                      Sever
                    </span>
                  )}
                </div>
                {code.eurocat_subgroup && (
                  <div className="text-xs text-foreground/60 mt-1">
                    EUROCAT: {code.eurocat_subgroup}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
